import { Input, Select, Typography } from "antd";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { StepOutcome } from "components/CC/types/enums";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect } from "react";
import { FormItem } from "react-hook-form-antd";
import { z } from "zod";

const endFormSchema = z.object({
    active: z.boolean(),
    title: z.string(),
    subtitle: z.string(),
    success: z.nativeEnum(StepOutcome),
});

export type EndFormData = z.infer<typeof endFormSchema>;

interface FormProps {
    onChange?: (data: EndFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
}

export const EndForm = ({ onChange, onRegisterValidator, formId }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    return (
        <GeneralFormProvider<EndFormData>
            key={selectedNode?.id}
            id={formId}
            schema={endFormSchema}
            defaultValues={selectedNode?.formData as EndFormData}
        >
            <FormContent onChange={onChange} onRegisterValidator={onRegisterValidator} />
        </GeneralFormProvider>
    );
};

const FormContent = ({
    onChange,
    onRegisterValidator,
}: {
    onChange?: (data: EndFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
}) => {
    const { control, trigger } = useGeneralForm<EndFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { t } = usePaldockTranslation("components.connection-creator");

    return (
        <div className={"bg-white rounded-lg p-3 m-3"}>
            <FormValueSync<EndFormData> onChange={onChange} />
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.end.forms.settings")}
                </Typography.Title>
            </div>
            <div className="flex gap-4 mb-4">
                <FormItem className="flex-1 !mb-0" control={control} name="title" label={t("nodes.end.forms.title")}>
                    <Input placeholder={t("nodes.end.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.end.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.end.forms.subtitle_placeholder")} />
                </FormItem>
            </div>
            <FormItem className="!mb-0" control={control} name="success" label={t("nodes.end.forms.outcome")}>
                <Select
                    options={[
                        { value: StepOutcome.Success, label: t("nodes.end.forms.outcome_success") },
                        { value: StepOutcome.Failed, label: t("nodes.end.forms.outcome_failed") },
                    ]}
                    placeholder={t("nodes.end.forms.outcome_placeholder")}
                />
            </FormItem>
        </div>
    );
};
